// Production Booking — the booked/planned production runs. The whole point is to make it clear
// exactly what's being produced, when, by whom, and its live status.
import { useEffect, useMemo, useState } from "react";
import type { DemandRow, Run, RunItem, RunStatus, Sku, LabelCheck } from "@shared/types.ts";
import type { RunRecommendation } from "@shared/engine.ts";
import { RUN_STATUSES, isIncoming, recommendMissingRuns, DEFAULT_PLANNING, defaultRunSizing, projectStockoutDay, projectFinalStockoutDay } from "@shared/engine.ts";
import type { Planning } from "@shared/types.ts";
import { api } from "../lib/api.ts";
import { pushNotif } from "@shared/notify.ts";
import { useData } from "../state/store.tsx";
import { id as newId } from "../lib/ids.ts";
import { Badge, EmptyState, Modal, LabeledInput, SegTabs } from "../components/ui.tsx";
import { fmtDate, num, todayISO } from "../lib/format.ts";

const LABEL_CHECKS = ["Strength / mg matches the SKU", "Flavour name correct", "Net volume correct", "Health & legal warnings present", "Barcode present", "Ingredients & allergens listed"];
const fileToDataUrl = (f: File) => new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("read failed")); r.readAsDataURL(f); });

const STATUS = RUN_STATUSES;
type Tone = "ok" | "danger" | "brand" | "warn" | "muted";
const statusTone = (s: RunStatus): Tone =>
  s === "Delivered" ? "ok"
    : s === "Cancelled" ? "danger"
    : s === "In Production" || s === "Waiting Pickup" ? "brand"
    : s === "Planned" ? "muted"
    : "warn";
const toneDot: Record<Tone, string> = { ok: "bg-ok", danger: "bg-danger", brand: "bg-brand", warn: "bg-warn", muted: "bg-muted" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function Booking({ initialItems, onConsumeInitial }: { initialItems?: RunItem[] | null; onConsumeInitial?: () => void } = {}) {
  const { data } = useData();
  const [editing, setEditing] = useState<Run | null>(null);
  const [creating, setCreating] = useState(false);
  const [seedItems, setSeedItems] = useState<RunItem[] | null>(null);
  const [seedDate, setSeedDate] = useState<string | null>(null);
  const [view, setView] = useState<"list" | "calendar" | "timeline">("list");
  const [demand, setDemand] = useState<DemandRow[]>([]);

  const bizId = data?.business.id;
  useEffect(() => {
    if (!bizId) return;
    api.demand(bizId).then((s) => setDemand(s.rows || [])).catch(() => setDemand([]));
  }, [bizId]);

  useEffect(() => {
    if (initialItems && initialItems.length) {
      setSeedItems(initialItems);
      setCreating(true);
      onConsumeInitial?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems]);

  if (!data) return null;
  const unit = data.business.unitLabel;
  const runs = [...data.runs].sort((a, b) => (a.scheduledDate < b.scheduledDate ? 1 : -1));
  const skuName = (skuId: string) => data.skus.find((s) => s.id === skuId)?.name || skuId;
  const cannerName = (cid: string) => data.canners.find((c) => c.id === cid)?.name || "—";

  // "Always keep N productions booked" rule + recommended missing runs (from demand).
  const target = data.business.targetBookedRuns ?? 2;
  const planning = data.business.planning || DEFAULT_PLANNING;
  const activeBooked = data.runs.filter((r) => isIncoming(r.status)).length;
  const missing = Math.max(0, target - activeBooked);
  const sizing = data.business.runSizing || defaultRunSizing(data.business.market);
  const recs = recommendMissingRuns(demand, activeBooked, planning, target, todayISO(), sizing);
  const bookRec = (rec: RunRecommendation) => { setSeedItems(rec.items); setSeedDate(rec.date); setCreating(true); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold">Booking</h2>
          <p className="text-muted text-xs mt-0.5">Booked and planned production runs.</p>
        </div>
        <div className="flex items-center gap-2">
          <SegTabs tabs={[{ id: "list", label: "List" }, { id: "calendar", label: "Calendar" }, { id: "timeline", label: "Timeline" }]} value={view} onChange={setView} />
          <button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Book run</button>
        </div>
      </div>

      {/* "Always keep N booked" status */}
      <div className={`card flex items-center justify-between gap-3 ${missing === 0 ? "border-ok/30 bg-ok/5" : missing >= target ? "border-danger/30 bg-danger/5" : "border-warn/30 bg-warn/5"}`}>
        <div className="text-sm">
          {missing === 0
            ? <span className="text-ok font-medium">✓ {activeBooked} of {target} productions booked</span>
            : <span className={missing >= target ? "text-danger font-medium" : "text-warn font-medium"}>⚠ {activeBooked} of {target} productions booked — {missing} missing</span>}
          <span className="text-muted ml-2 text-xs">You keep {target} in the pipeline at all times.</span>
        </div>
      </div>

      {/* Recommended missing productions (timing + quantities) */}
      {recs.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3">
          {recs.map((rec) => (
            <div key={rec.slot} className="card border-dashed border-brand/40 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm">Recommended production {rec.slot + 1}</div>
                <Badge tone="brand">{rec.basis === "catch-up" ? "catch up" : "next cycle"}</Badge>
              </div>
              <div className="text-xs text-muted">
                {rec.basis === "catch-up" ? "As soon as possible" : `Target ${fmtDate(rec.date)}`} · <span className="font-medium text-ink">{num(rec.total)} {unit}s</span>
                {rec.days > 1 && <span> · {rec.days} production days</span>}
                {rec.total !== rec.rawTotal && <span className="text-muted/80"> · rounded up from {num(rec.rawTotal)} of demand</span>}
              </div>
              <div className="flex flex-wrap gap-1">
                {rec.items.slice(0, 8).map((i) => <span key={i.skuId} className="tag">{skuName(i.skuId)}: {num(i.cans)}</span>)}
                {rec.items.length > 8 && <span className="tag">+{rec.items.length - 8}</span>}
              </div>
              <div className="flex justify-end"><button className="btn-primary btn-sm" onClick={() => bookRec(rec)}>Book this run →</button></div>
            </div>
          ))}
        </div>
      )}

      {view === "timeline" ? (
        <RunwayTimeline demand={demand} runs={runs} planning={planning} recs={recs} skuName={skuName} unit={unit} />
      ) : runs.length === 0 ? (
        <div className="card"><EmptyState title="No production runs yet" sub="Book your first run — or use a recommended production above." action={<button className="btn-primary btn-sm" onClick={() => setCreating(true)}>+ Book run</button>} /></div>
      ) : view === "calendar" ? (
        <YearCalendar runs={runs} unit={unit} onOpen={setEditing} recs={recs} onBookRec={bookRec} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {runs.map((r) => {
            const total = r.items.reduce((a, i) => a + i.cans, 0);
            const order = data.orders.find((o) => o.id === r.orderId);
            return (
              <button key={r.id} onClick={() => setEditing(r)} className="card card-hover text-left space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm">{fmtDate(r.scheduledDate)}</div>
                  <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                </div>
                <div className="text-xs text-muted">{cannerName(r.cannerId)}</div>
                <div className="text-sm">{num(total)} {unit}s · {r.items.length} SKU{r.items.length !== 1 ? "s" : ""}</div>
                <div className="flex flex-wrap gap-1">
                  {r.items.slice(0, 5).map((i) => <span key={i.skuId} className="tag">{skuName(i.skuId)}: {num(i.cans)}</span>)}
                  {r.items.length > 5 && <span className="tag">+{r.items.length - 5}</span>}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-muted pt-1">
                  {r.poNumber && <span>PO {r.poNumber}</span>}
                  {r.batchNumber && <span>· Batch {r.batchNumber}</span>}
                  {order && <span>· Order {order.status}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <RunModal run={editing} initialItems={seedItems} initialDate={seedDate} onClose={() => { setCreating(false); setEditing(null); setSeedItems(null); setSeedDate(null); }} />
      )}
    </div>
  );
}

// High-level year calendar — 12 month cells, each listing its runs (colour-coded by status) plus
// dashed placeholders for recommended (missing) productions in their target month.
function YearCalendar({ runs, unit, onOpen, recs, onBookRec }: { runs: Run[]; unit: string; onOpen: (r: Run) => void; recs: RunRecommendation[]; onBookRec: (rec: RunRecommendation) => void }) {
  const years = Array.from(new Set(runs.map((r) => Number((r.scheduledDate || "").slice(0, 4)) || 0).filter(Boolean)));
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(years.includes(thisYear) ? thisYear : (years.sort()[years.length - 1] || thisYear));
  const recsByMonth: RunRecommendation[][] = Array.from({ length: 12 }, () => []);
  for (const rec of recs) { if (Number(rec.date.slice(0, 4)) === year) { const m = Number(rec.date.slice(5, 7)) - 1; if (m >= 0 && m < 12) recsByMonth[m].push(rec); } }

  const byMonth: Run[][] = Array.from({ length: 12 }, () => []);
  for (const r of runs) {
    const d = r.scheduledDate || "";
    if (Number(d.slice(0, 4)) !== year) continue;
    const m = Number(d.slice(5, 7)) - 1;
    if (m >= 0 && m < 12) byMonth[m].push(r);
  }
  for (const list of byMonth) list.sort((a, b) => (a.scheduledDate < b.scheduledDate ? -1 : 1));
  const yearTotal = byMonth.flat().reduce((a, r) => a + r.items.reduce((x, i) => x + i.cans, 0), 0);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setYear((y) => y - 1)}>‹</button>
        <div className="text-sm font-semibold">{year} <span className="text-muted font-normal">· {byMonth.flat().length} runs · {num(yearTotal)} {unit}s</span></div>
        <button className="btn-sm border border-edge hover:bg-paper" onClick={() => setYear((y) => y + 1)}>›</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {MONTHS.map((mn, mi) => {
          const list = byMonth[mi];
          const isNow = year === thisYear && mi === new Date().getMonth();
          return (
            <div key={mn} className={`rounded-lg border p-2 min-h-[84px] ${isNow ? "border-brand/40 bg-brandSoft/40" : "border-edge/70"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-medium ${isNow ? "text-brand" : "text-muted"}`}>{mn}</span>
                {list.length > 0 && <span className="text-[10px] text-muted">{num(list.reduce((a, r) => a + r.items.reduce((x, i) => x + i.cans, 0), 0))}</span>}
              </div>
              <div className="space-y-1">
                {list.map((r) => (
                  <button key={r.id} onClick={() => onOpen(r)} title={`${fmtDate(r.scheduledDate)} · ${r.status}`}
                    className="w-full flex items-center gap-1.5 text-left px-1.5 py-1 rounded bg-paper hover:bg-panel border border-edge/60">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${toneDot[statusTone(r.status)]}`} />
                    <span className="text-[11px] tabular-nums shrink-0">{Number((r.scheduledDate || "").slice(8, 10))}</span>
                    <span className="text-[11px] text-muted truncate">{num(r.items.reduce((a, i) => a + i.cans, 0))}</span>
                  </button>
                ))}
                {recsByMonth[mi].map((rec) => (
                  <button key={`rec${rec.slot}`} onClick={() => onBookRec(rec)} title={`Recommended: ${num(rec.total)} ${unit}s — click to book`}
                    className="w-full flex items-center gap-1.5 text-left px-1.5 py-1 rounded border border-dashed border-brand/50 text-brand hover:bg-brandSoft">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0 border border-brand" />
                    <span className="text-[11px] truncate">+ {num(rec.total)}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-[11px] text-muted">
        {(["Planned", "Booked", "In Production", "Delivered", "Cancelled"] as RunStatus[]).map((s) => (
          <span key={s} className="inline-flex items-center gap-1"><span className={`w-1.5 h-1.5 rounded-full ${toneDot[statusTone(s)]}`} />{s}</span>
        ))}
      </div>
    </div>
  );
}

function RunModal({ run, initialItems, initialDate, onClose }: { run: Run | null; initialItems?: RunItem[] | null; initialDate?: string | null; onClose: () => void }) {
  const { data, mutate, saving } = useData();
  const d = data!;
  const unit = d.business.unitLabel;
  const activeSkus = d.skus.filter((s) => !s.discontinued && !s.assembled);
  const [scheduledDate, setScheduledDate] = useState(run?.scheduledDate || initialDate || todayISO());
  const [cannerId, setCannerId] = useState(run?.cannerId || d.canners[0]?.id || "");
  const [poNumber, setPoNumber] = useState(run?.poNumber || "");
  const [batchNumber, setBatchNumber] = useState(run?.batchNumber || "");
  const [status, setStatus] = useState<RunStatus>(run?.status || "Booked");
  const [actualYield, setActualYield] = useState<string>(run?.actualYield != null ? String(run.actualYield) : "");
  const [finishedDate, setFinishedDate] = useState(run?.finishedDate || "");
  const [notes, setNotes] = useState(run?.notes || "");
  const [manualChecks, setManualChecks] = useState<Record<string, boolean>>({});
  const [cans, setCans] = useState<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const it of run?.items || initialItems || []) m[it.skuId] = it.cans;
    return m;
  });

  const byFlavour = useMemo(() => {
    const m = new Map<string, typeof activeSkus>();
    for (const s of activeSkus) { if (!m.has(s.flavour)) m.set(s.flavour, []); m.get(s.flavour)!.push(s); }
    return Array.from(m.entries());
  }, [activeSkus]);

  const items: RunItem[] = Object.entries(cans).map(([skuId, c]) => ({ skuId, cans: Number(c) || 0 })).filter((i) => i.cans > 0);
  const total = items.reduce((a, i) => a + i.cans, 0);

  // Snapshot which label version each SKU is on right now — recorded on the run for traceability.
  const labelVersions = () => {
    const lv: Record<string, number> = {};
    for (const it of items) { const s = d.skus.find((x) => x.id === it.skuId); if (s?.label) lv[it.skuId] = s.label.version; }
    return Object.keys(lv).length ? lv : undefined;
  };

  const save = async () => {
    await mutate((draft) => {
      if (run) {
        const target = draft.runs.find((x) => x.id === run.id);
        if (target) {
          const dateChanged = target.scheduledDate !== scheduledDate;
          const wasAgreed = target.schedule?.state === "confirmed" || target.status !== "Planned";
          target.scheduledDate = scheduledDate; target.cannerId = cannerId; target.items = items;
          target.poNumber = poNumber || undefined; target.batchNumber = batchNumber || undefined;
          target.notes = notes || undefined;
          target.labelVersions = labelVersions();
          target.actualYield = actualYield === "" ? undefined : Number(actualYield);
          target.finishedDate = finishedDate || undefined;
          if (dateChanged && wasAgreed) {
            // Moving an already-agreed date re-opens the handshake — the manufacturer must re-confirm.
            const now = Date.now();
            target.schedule = { state: "proposed", awaiting: "mfr", thread: [...(target.schedule?.thread || []), { at: now, by: "owner", date: scheduledDate, kind: "counter", note: "start date changed" }] };
            target.status = "Planned";
            pushNotif(draft, { audience: "mfr", kind: "date_proposed", tone: "warn", runId: target.id, at: now,
              title: "Start date changed", body: `Little Rick moved the run to ${scheduledDate}. Confirm or suggest another date.` });
          } else {
            target.status = status;
          }
        }
      } else {
        // A new run is a *proposal* to the manufacturer — starts as Planned, awaiting their confirmation.
        const now = Date.now();
        const nr: Run = {
          id: newId("run"), businessId: d.business.id, scheduledDate, cannerId, items,
          status: "Planned", poNumber: poNumber || undefined, batchNumber: batchNumber || undefined,
          notes: notes || undefined, createdAt: now, labelVersions: labelVersions(),
          schedule: { state: "proposed", awaiting: "mfr", thread: [{ at: now, by: "owner", date: scheduledDate, kind: "propose" }] },
        };
        draft.runs.push(nr);
        pushNotif(draft, { audience: "mfr", kind: "date_proposed", tone: "warn", runId: nr.id, at: now,
          title: "New production proposed", body: `Little Rick proposed a run for ${scheduledDate} (${total} ${unit}s). Confirm or suggest another date.` });
      }
    });
    onClose();
  };

  const remove = async () => {
    if (!run || !confirm("Delete this run?")) return;
    await mutate((draft) => { draft.runs = draft.runs.filter((x) => x.id !== run.id); });
    onClose();
  };

  return (
    <Modal title={run ? "Edit production run" : "Book production run"} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <LabeledInput label="Scheduled date"><input type="date" className="input" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} /></LabeledInput>
          <LabeledInput label="Canner / facility">
            <select className="input" value={cannerId} onChange={(e) => setCannerId(e.target.value)}>
              {d.canners.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              {d.canners.length === 0 && <option value="">Add a canner in Admin</option>}
            </select>
          </LabeledInput>
          <LabeledInput label="Tolling PO #"><input className="input" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} /></LabeledInput>
          <LabeledInput label="Batch / lot #"><input className="input" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} /></LabeledInput>
        </div>

        <div>
          <span className="label">Production quantities ({unit}s)</span>
          <div className="grid sm:grid-cols-2 gap-3">
            {byFlavour.map(([flavour, skus]) => (
              <div key={flavour} className="rounded-lg border border-edge/70 p-3">
                <div className="text-sm font-medium mb-2">{flavour}</div>
                <div className="space-y-1.5">
                  {skus.map((s) => (
                    <label key={s.id} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-muted">{s.strength}</span>
                      <input type="number" min={0} step={100} className="input w-28 py-1 text-right" placeholder="0"
                        value={cans[s.id] ?? ""} onChange={(e) => setCans((c) => ({ ...c, [s.id]: Number(e.target.value) }))} />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-xs text-muted mt-2">Total: {num(total)} {unit}s across {items.length} SKUs</div>
        </div>

        {items.length > 0 && (
          <div className="rounded-lg border border-edge/70 p-3 space-y-3">
            <div>
              <span className="label mb-0">Labels — confirm the artwork before it goes to print</span>
              <p className="text-[11px] text-muted mt-0.5">For each SKU, check the latest label is attached and correct. Upload a new version if the artwork changed, and run the error check.</p>
            </div>
            {items.map((it) => { const s = d.skus.find((x) => x.id === it.skuId); return s ? <LabelRow key={it.skuId} sku={s} businessId={d.business.id} /> : null; })}
            <div className="border-t border-edge/60 pt-2.5 space-y-1">
              <div className="text-[11px] font-semibold text-muted uppercase tracking-wide">Final manual check</div>
              {LABEL_CHECKS.map((item) => (
                <label key={item} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!manualChecks[item]} onChange={(e) => setManualChecks((m) => ({ ...m, [item]: e.target.checked }))} /> {item}</label>
              ))}
            </div>
          </div>
        )}

        {run ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-edge">
            <LabeledInput label="Status" hint="Set the current stage — e.g. In Production for a run already underway">
              <select className="input" value={status} onChange={(e) => setStatus(e.target.value as RunStatus)}>
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </LabeledInput>
            <LabeledInput label="Actual yield"><input type="number" className="input" value={actualYield} onChange={(e) => setActualYield(e.target.value)} /></LabeledInput>
            <LabeledInput label="Finished date"><input type="date" className="input" value={finishedDate} onChange={(e) => setFinishedDate(e.target.value)} /></LabeledInput>
          </div>
        ) : (
          <div className="pt-2 border-t border-edge text-xs text-muted">This books a <span className="font-medium text-ink">proposed</span> run — {d.canners[0]?.name || "the manufacturer"} confirms the start date (or suggests another) in their portal before it enters production.</div>
        )}

        <LabeledInput label="Notes"><textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></LabeledInput>

        <div className="flex justify-between items-center pt-1">
          {run ? <button className="btn-sm text-danger hover:bg-danger/5" onClick={remove}>Delete</button> : <span />}
          <div className="flex gap-2">
            <button className="btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-sm" disabled={saving || !cannerId || items.length === 0} onClick={save}>{saving ? "…" : "Save run"}</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// One SKU's label: current version + "any changes?" upload + AI/error check.
function LabelRow({ sku, businessId }: { sku: Sku; businessId: string }) {
  const { mutate } = useData();
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<LabelCheck | null>(sku.label?.check || null);
  const [answer, setAnswer] = useState<null | "same" | "new">(null); // "any changes?" response
  const label = sku.label;

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { key, url } = await api.upload(businessId, dataUrl);
      await mutate((draft) => {
        const s = draft.skus.find((x) => x.id === sku.id);
        if (s) s.label = { key, url, fileName: file.name, version: (s.label?.version || 0) + 1, uploadedAt: Date.now() };
      });
      setAnswer("new"); setCheck(null);
    } finally { setBusy(false); }
  };
  const runCheck = async () => {
    setBusy(true);
    try { setCheck(await api.labelCheck(businessId, sku.id)); } finally { setBusy(false); }
  };
  const errors = check?.issues.filter((i) => i.severity === "error").length || 0;
  const warns = check?.issues.filter((i) => i.severity === "warning").length || 0;

  return (
    <div className="rounded-lg border border-edge/60 bg-paper/50 p-2.5 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex-1 min-w-0">{sku.name}</span>
        {label
          ? <a href={label.url} target="_blank" rel="noreferrer" className="text-[11px] text-brand hover:underline shrink-0">Label v{label.version} ↗</a>
          : <span className="text-[11px] text-warn shrink-0">No label yet</span>}
      </div>

      {!label && (
        <label className="btn-sm border border-edge hover:bg-paper cursor-pointer inline-block">{busy ? "Uploading…" : "Upload label (PDF/image)"}
          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      )}
      {label && answer === null && (
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="text-muted">Any changes since last time?</span>
          <button type="button" className="btn-sm border border-edge hover:bg-paper" onClick={() => setAnswer("same")}>No — use current</button>
          <label className="btn-sm border border-edge hover:bg-paper cursor-pointer">Yes — upload new<input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} /></label>
        </div>
      )}
      {label && answer === "same" && <div className="text-[11px] text-ok">Using current label v{label.version} ✓ <button type="button" className="text-muted hover:underline ml-1" onClick={() => setAnswer(null)}>change</button></div>}
      {label && answer === "new" && <div className="text-[11px] text-ok">New label uploaded — now v{label.version} ✓</div>}

      {label && (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" className="btn-sm border border-edge hover:bg-paper" disabled={busy} onClick={runCheck}>{busy ? "Checking…" : "Run error check"}</button>
          {check && check.status === "pass" && <Badge tone="ok">No issues</Badge>}
          {check && check.status === "issues" && <Badge tone={errors ? "danger" : "warn"}>{errors ? `${errors} error${errors !== 1 ? "s" : ""}` : ""}{errors && warns ? ", " : ""}{warns ? `${warns} warning${warns !== 1 ? "s" : ""}` : ""}</Badge>}
          {check && check.status === "error" && <Badge tone="muted">Check unavailable</Badge>}
        </div>
      )}
      {check?.summary && <div className="text-[11px] text-muted">{check.summary}</div>}
      {check && check.issues.length > 0 && (
        <ul className="text-[11px] space-y-0.5">
          {check.issues.map((iss, i) => <li key={i} className={iss.severity === "error" ? "text-danger" : "text-warn"}>• {iss.text}</li>)}
        </ul>
      )}
    </div>
  );
}

// Stock-runway timeline — per SKU, a bar showing days of cover at current sales (including booked
// runs), a ◆ marking when to START the next production so it lands before safety stock runs out
// (stockout-to-safety date − lead time), and ticks for booked (solid) / recommended (dashed) runs.
function RunwayTimeline({ demand, runs, planning, recs, skuName, unit }: {
  demand: DemandRow[]; runs: Run[]; planning: Planning; recs: RunRecommendation[]; skuName: (id: string) => string; unit: string;
}) {
  const [sel, setSel] = useState<string | null>(null);
  const today = new Date(todayISO() + "T00:00:00");
  const dayOf = (iso: string) => Math.round((new Date(iso + "T00:00:00").getTime() - today.getTime()) / 86400000);
  const dateAt = (day: number) => { const d = new Date(today); d.setDate(d.getDate() + Math.round(day)); return d; };
  const fmtShort = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

  const rows = demand
    .filter((r) => r.trigger !== "DISCONTINUED" && r.dailyUnits > 0)
    .map((r) => {
      const onHand = r.inventory + (Number(r.manufacturerStock) || 0);
      const booked = runs.filter((rn) => isIncoming(rn.status))
        .map((rn) => ({ day: Math.max(0, dayOf(rn.readyDate || rn.scheduledDate)), qty: rn.items.filter((i) => i.skuId === r.skuId).reduce((a, i) => a + i.cans, 0) }))
        .filter((a) => a.qty > 0);
      const rec = recs.flatMap((rc) => { const q = rc.items.filter((i) => i.skuId === r.skuId).reduce((a, i) => a + i.cans, 0); return q > 0 ? [{ day: Math.max(0, dayOf(rc.date)), qty: q }] : []; });
      const stockout = projectStockoutDay(onHand, r.dailyUnits, booked, 0);
      // Reorder point = the FINAL safety crossing (after all booked runs land), minus lead time — so a
      // booked run that covers an early transient dip doesn't falsely flag "overdue".
      const safetyDay = projectFinalStockoutDay(onHand, r.dailyUnits, booked, planning.safetyStockDays * r.dailyUnits);
      const reorderBy = safetyDay - planning.leadTimeDays;
      return { r, booked, rec, stockout, reorderBy };
    })
    .sort((a, b) => a.stockout - b.stockout);

  if (!rows.length) return <div className="card"><EmptyState title="No sales velocity yet" sub="Once SKUs have sales data, this shows how long stock lasts and when to start the next run." /></div>;

  const W = 980, L = 152, R = 912, top = 58, rowH = 40;
  const H = top + rows.length * rowH + 20;
  const horizonRaw = Math.max(120, ...rows.flatMap((x) => [x.stockout, x.reorderBy, ...x.booked.map((b) => b.day), ...x.rec.map((b) => b.day)]).filter((n) => Number.isFinite(n)));
  const horizon = Math.min(600, Math.ceil((horizonRaw + 30) / 30) * 30);
  const x = (day: number) => L + (Math.max(0, Math.min(day, horizon)) / horizon) * (R - L);

  const ticks: { d: number; label: string }[] = [];
  for (let d = 0; d <= horizon; d += 30) { const dt = dateAt(d); ticks.push({ d, label: `${MONTHS[dt.getMonth()]}${dt.getMonth() === 0 ? " " + String(dt.getFullYear()).slice(2) : ""}` }); }
  const barTone = (reorderBy: number) => (reorderBy <= 0 ? "fill-danger" : reorderBy < 30 ? "fill-warn" : "fill-ok");

  return (
    <div className="card space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold">Stock runway</h3>
        <div className="flex items-center gap-3 text-[11px] text-muted">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm bg-ok" /> cover</span>
          <span className="flex items-center gap-1"><span className="inline-block w-0.5 h-3 bg-brand" /> booked</span>
          <span className="flex items-center gap-1"><span className="inline-block w-0.5 h-3 bg-brand opacity-60" style={{ borderLeft: "1px dashed" }} /> recommended</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rotate-45 bg-warn" /> start next by</span>
        </div>
      </div>
      <p className="text-muted text-xs">Each bar = days of stock at current sales, including booked runs. ◆ marks when to <span className="font-medium">start</span> the next production so it lands in time (lead {planning.leadTimeDays}d + {planning.safetyStockDays}d safety). Red = overdue or due within a month. <span className="text-brand">Click a SKU</span> for the depletion &amp; replenishment chart.</p>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 680 }} className="select-none">
          {ticks.map((t) => (
            <g key={t.d}>
              <line x1={x(t.d)} y1={top - 10} x2={x(t.d)} y2={H - 6} className="stroke-edge" strokeWidth={1} opacity={0.4} />
              <text x={x(t.d)} y={top - 16} textAnchor="middle" fontSize={10} className="fill-muted">{t.label}</text>
            </g>
          ))}
          <line x1={x(0)} y1={top - 10} x2={x(0)} y2={H - 6} className="stroke-ink" strokeWidth={1.5} />
          <text x={x(0)} y={top - 30} textAnchor="middle" fontSize={10} className="fill-ink font-medium">today</text>

          {rows.map((row, i) => {
            const cy = top + i * rowH + 14;
            const soX = x(row.stockout);
            const overshoot = row.stockout > horizon;
            const co = dateAt(row.stockout);
            return (
              <g key={row.r.skuId} className="cursor-pointer" onClick={() => setSel(row.r.skuId)}>
                <rect x={0} y={cy - rowH / 2} width={W} height={rowH} className="fill-ink" opacity={0} />
                <text x={6} y={cy + 3} fontSize={11} className="fill-ink font-medium">{skuName(row.r.skuId).slice(0, 24)}</text>
                <rect x={L} y={cy - 7} width={R - L} height={14} rx={7} className="fill-edge" opacity={0.35} />
                <rect x={L} y={cy - 7} width={Math.max(1, (overshoot ? R : soX) - L)} height={14} rx={7} className={barTone(row.reorderBy)} opacity={0.85}>
                  <title>{`Covered until ${fmtShort(co)} (~${Math.round(row.stockout)} days)`}</title>
                </rect>
                {row.booked.map((b, k) => (
                  <g key={`b${k}`}><line x1={x(b.day)} y1={cy - 11} x2={x(b.day)} y2={cy + 11} className="stroke-brand" strokeWidth={2.5} /><title>{`Booked ${num(b.qty)} ${unit}s ~${fmtShort(dateAt(b.day))}`}</title></g>
                ))}
                {row.rec.map((b, k) => (
                  <line key={`r${k}`} x1={x(b.day)} y1={cy - 10} x2={x(b.day)} y2={cy + 10} className="stroke-brand" strokeWidth={1.5} strokeDasharray="2 2" opacity={0.6} />
                ))}
                {row.reorderBy > 0 && row.reorderBy <= horizon
                  ? <rect x={x(row.reorderBy) - 5} y={cy - 5} width={10} height={10} className={row.reorderBy < 30 ? "fill-danger" : "fill-warn"} transform={`rotate(45 ${x(row.reorderBy)} ${cy})`}><title>{`Start next production by ${fmtShort(dateAt(row.reorderBy))}`}</title></rect>
                  : row.reorderBy <= 0 ? <g><circle cx={L - 9} cy={cy} r={5} className="fill-danger" /><title>Overdue — start the next production now</title></g> : null}
                <text x={Math.min(overshoot ? R : soX, R) + 10} y={cy + 3} fontSize={10} className="fill-muted">{overshoot ? `${fmtShort(co)} ▸` : `${fmtShort(co)} · ${Math.round(row.stockout)}d`}</text>
              </g>
            );
          })}
        </svg>
      </div>
      {sel && (() => {
        const row = rows.find((x) => x.r.skuId === sel);
        if (!row) return null;
        return (
          <Modal wide title={`${skuName(sel)} — stock projection`} onClose={() => setSel(null)}>
            <SkuRunwayChart row={row} planning={planning} unit={unit} today={today} />
          </Modal>
        );
      })()}
    </div>
  );
}

// Per-SKU deep-dive: units-on-hand over time. Solid line = booked plan (on-hand depleting at the sale
// rate, stepping up as each booked run lands); dashed line = the same PLUS recommended runs. Red
// dashed = safety-stock level; the shaded band below it is the danger zone.
function SkuRunwayChart({ row, planning, unit, today }: {
  row: { r: DemandRow; booked: { day: number; qty: number }[]; rec: { day: number; qty: number }[]; stockout: number; reorderBy: number };
  planning: Planning; unit: string; today: Date;
}) {
  const { r } = row;
  const daily = r.dailyUnits;
  const onHand = r.inventory + (Number(r.manufacturerStock) || 0);
  const safety = planning.safetyStockDays * daily;
  const dateAt = (day: number) => { const d = new Date(today); d.setDate(d.getDate() + Math.round(day)); return d; };
  const fmtShort = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

  const allDays = [row.stockout, row.reorderBy, ...row.booked.map((b) => b.day), ...row.rec.map((b) => b.day)].filter((n) => Number.isFinite(n));
  const horizon = Math.min(720, Math.max(180, Math.ceil((Math.max(0, ...allDays) + 45) / 30) * 30));

  // Piecewise stock curve with exact zero-crossings.
  const build = (arrivals: { day: number; qty: number }[]) => {
    const evs = arrivals.filter((a) => a.day >= 0 && a.day <= horizon).sort((a, b) => a.day - b.day);
    const pts: { d: number; v: number }[] = [];
    let cur = Math.max(0, onHand), t = 0;
    pts.push({ d: 0, v: cur });
    const declineTo = (target: number) => {
      if (daily > 0 && cur > 0) {
        const zeroDay = t + cur / daily;
        if (zeroDay < target) { pts.push({ d: zeroDay, v: 0 }); cur = 0; t = zeroDay; }
      }
      cur = Math.max(0, cur - daily * (target - t));
      pts.push({ d: target, v: cur }); t = target;
    };
    for (const ev of evs) { declineTo(ev.day); cur += ev.qty; pts.push({ d: ev.day, v: cur }); }
    declineTo(horizon);
    return pts;
  };
  const booked = build(row.booked);
  const planned = build([...row.booked, ...row.rec]);

  const W = 720, H = 300, L = 48, Rr = 700, T = 16, B = 250;
  const yMax = Math.max(onHand, safety * 1.3, ...booked.map((p) => p.v), ...planned.map((p) => p.v), 1) * 1.08;
  const X = (d: number) => L + (d / horizon) * (Rr - L);
  const Y = (v: number) => B - (v / yMax) * (B - T);
  const path = (pts: { d: number; v: number }[]) => pts.map((p, i) => `${i ? "L" : "M"}${X(p.d).toFixed(1)} ${Y(p.v).toFixed(1)}`).join(" ");

  const monthTicks: number[] = [];
  for (let d = 0; d <= horizon; d += Math.max(30, Math.round(horizon / 12 / 30) * 30)) monthTicks.push(d);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round((yMax * f) / 100) * 100);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div><div className="text-muted">On hand</div><div className="font-semibold tabular-nums">{num(onHand)} {unit}s</div></div>
        <div><div className="text-muted">Sales / day</div><div className="font-semibold tabular-nums">{num(Math.round(daily))} {unit}s</div></div>
        <div><div className="text-muted">Covered until</div><div className="font-semibold">{fmtShort(dateAt(row.stockout))} <span className="text-muted font-normal">· {Math.round(row.stockout)}d</span></div></div>
        <div><div className="text-muted">Start next by</div><div className={`font-semibold ${row.reorderBy <= 30 ? "text-danger" : ""}`}>{row.reorderBy <= 0 ? "now (overdue)" : fmtShort(dateAt(row.reorderBy))}</div></div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 560 }}>
          {/* y gridlines + labels */}
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={L} y1={Y(v)} x2={Rr} y2={Y(v)} className="stroke-edge" strokeWidth={1} opacity={0.4} />
              <text x={L - 6} y={Y(v) + 3} textAnchor="end" fontSize={9} className="fill-muted">{num(v)}</text>
            </g>
          ))}
          {/* danger zone below safety */}
          <rect x={L} y={Y(safety)} width={Rr - L} height={Math.max(0, B - Y(safety))} className="fill-danger" opacity={0.07} />
          <line x1={L} y1={Y(safety)} x2={Rr} y2={Y(safety)} className="stroke-danger" strokeWidth={1} strokeDasharray="4 3" opacity={0.7} />
          <text x={Rr} y={Y(safety) - 3} textAnchor="end" fontSize={9} className="fill-danger" opacity={0.8}>safety {num(Math.round(safety))}</text>
          {/* x month labels */}
          {monthTicks.map((d) => (
            <g key={d}>
              <line x1={X(d)} y1={T} x2={X(d)} y2={B} className="stroke-edge" strokeWidth={1} opacity={0.25} />
              <text x={X(d)} y={B + 14} textAnchor="middle" fontSize={9} className="fill-muted">{fmtShort(dateAt(d))}</text>
            </g>
          ))}
          {/* planned (booked + recommended) dashed */}
          <path d={path(planned)} className="stroke-ok" fill="none" strokeWidth={1.5} strokeDasharray="5 3" opacity={0.85} />
          {/* booked-only solid */}
          <path d={path(booked)} className="stroke-brand" fill="none" strokeWidth={2} />
          {/* arrival markers */}
          {row.booked.map((b, k) => <g key={`b${k}`}><line x1={X(b.day)} y1={T} x2={X(b.day)} y2={B} className="stroke-brand" strokeWidth={1} opacity={0.35} /><circle cx={X(b.day)} cy={T + 4} r={3} className="fill-brand" /><title>{`Booked +${num(b.qty)} ~${fmtShort(dateAt(b.day))}`}</title></g>)}
          {row.rec.map((b, k) => <g key={`r${k}`}><circle cx={X(b.day)} cy={T + 4} r={3} className="fill-ok" opacity={0.7} /><title>{`Recommended +${num(b.qty)} ~${fmtShort(dateAt(b.day))}`}</title></g>)}
        </svg>
      </div>
      <div className="flex items-center gap-4 text-[11px] text-muted">
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-brand" /> booked plan</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-ok" style={{ borderTop: "1px dashed" }} /> + recommended</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 h-0.5 bg-danger opacity-70" /> safety stock</span>
      </div>
    </div>
  );
}
